import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { on as wsOn } from "@/lib/socket";
import { relative } from "@/lib/format";
import { MessageSquare, Plus, Send } from "lucide-react";

export default function SimulatorWhatsApp() {
    const [contacts, setContacts] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [newPhone, setNewPhone] = useState("");
    const scrollRef = useRef(null);

    const loadContacts = () => api.get("/whatsapp/mock/contacts").then((r) => setContacts(r.data));
    const loadMessages = (phone) =>
        api.get("/whatsapp/mock/messages", { params: { phone } }).then((r) => {
            setMessages(r.data);
            setTimeout(() => {
                if (scrollRef.current)
                    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }, 30);
        });

    useEffect(() => {
        loadContacts();
        const off1 = wsOn("wa:outbound", (d) => {
            loadContacts();
            if (active && d.phone === active) loadMessages(active);
        });
        const off2 = wsOn("wa:inbound", (d) => {
            loadContacts();
            if (active && d.phone === active) loadMessages(active);
        });
        return () => {
            off1();
            off2();
        };
    }, [active]);

    useEffect(() => {
        if (active) loadMessages(active);
    }, [active]);

    const startChat = (phone) => {
        if (!phone) return;
        setActive(phone);
        // seed: trigger webhook with an empty hello if first time
        api.post("/whatsapp/webhook", { phone, text: "hi" }).finally(() => {
            loadContacts();
            loadMessages(phone);
        });
    };

    const sendText = async (e) => {
        e?.preventDefault?.();
        if (!text.trim() || !active) return;
        const body = text.trim();
        setText("");
        await api.post("/whatsapp/webhook", { phone: active, text: body });
        loadMessages(active);
    };

    const tapReply = async (reply_id, reply_title) => {
        if (!active) return;
        await api.post("/whatsapp/webhook", { phone: active, reply_id, reply_title });
        loadMessages(active);
    };

    return (
        <div className="h-[calc(100vh-6rem)] flex gap-4" data-testid="simulator-whatsapp">
            <aside className="w-64 shrink-0 bg-card border border-border rounded-md overflow-hidden flex flex-col">
                <div className="p-3 border-b border-border">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono mb-2">
                        Contacts
                    </div>
                    <div className="flex gap-2">
                        <input
                            data-testid="simulator-new-phone-input"
                            placeholder="+91..."
                            value={newPhone}
                            onChange={(e) => setNewPhone(e.target.value)}
                            className="flex-1 bg-background border border-border rounded-md px-2 py-1 text-xs focus:outline-none focus:border-primary"
                        />
                        <button
                            data-testid="simulator-new-phone-btn"
                            onClick={() => {
                                startChat(newPhone.trim());
                                setNewPhone("");
                            }}
                            className="px-2 py-1 bg-primary text-primary-foreground rounded-md"
                            title="New chat"
                        >
                            <Plus className="size-3.5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {contacts.length === 0 && (
                        <div className="text-xs text-muted-foreground p-4 text-center">
                            No chats yet. Start one above.
                        </div>
                    )}
                    {contacts.map((c) => (
                        <button
                            key={c.phone}
                            data-testid={`sim-contact-${c.phone}`}
                            onClick={() => setActive(c.phone)}
                            className={`w-full text-left px-3 py-2 border-b border-border hover:bg-secondary/60 ${
                                active === c.phone ? "bg-secondary" : ""
                            }`}
                        >
                            <div className="text-xs font-mono truncate">{c.phone}</div>
                            <div className="text-[11px] text-muted-foreground truncate">
                                {c.name || "Unknown"} · {c.company || ""}
                            </div>
                            <div className="text-[10px] text-muted-foreground truncate">
                                {c.last_body?.slice(0, 60) || ""}
                            </div>
                        </button>
                    ))}
                </div>
            </aside>
            <main className="flex-1 bg-card border border-border rounded-md flex flex-col overflow-hidden">
                {!active ? (
                    <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground flex-col gap-2">
                        <MessageSquare className="size-8" />
                        <div>Pick a contact or start a new chat to simulate a customer.</div>
                    </div>
                ) : (
                    <>
                        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="text-xs font-mono">{active}</div>
                                <div className="text-[11px] text-muted-foreground">
                                    WhatsApp (mock)
                                </div>
                            </div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                                {messages.length} msgs
                            </div>
                        </div>
                        <div
                            ref={scrollRef}
                            className="flex-1 wa-bg p-4 overflow-y-auto space-y-2"
                        >
                            {messages.map((m) => (
                                <SimBubble key={m.id} m={m} onTap={tapReply} />
                            ))}
                        </div>
                        <form
                            onSubmit={sendText}
                            className="p-3 border-t border-border flex gap-2"
                        >
                            <input
                                data-testid="simulator-chat-input"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Type a customer message…"
                                className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary"
                            />
                            <button
                                data-testid="simulator-chat-send"
                                className="px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm inline-flex items-center gap-1"
                            >
                                <Send className="size-3.5" /> Send
                            </button>
                        </form>
                    </>
                )}
            </main>
        </div>
    );
}

function SimBubble({ m, onTap }) {
    const out = m.direction === "OUT";
    const payload = m.payload || {};
    const body = m.body || "";
    return (
        <div className={`flex ${out ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[78%] rounded-md px-3 py-2 text-sm ${
                    out ? "wa-bubble-out" : "wa-bubble-in"
                }`}
            >
                {payload.header && (
                    <div className="text-[11px] opacity-70 mb-1 font-semibold">
                        {payload.header}
                    </div>
                )}
                <div className="whitespace-pre-wrap">{body}</div>
                {m.kind === "interactive_list" && (
                    <div className="mt-2 space-y-1">
                        {(payload.sections || []).flatMap((s) => s.rows || []).map((r) => (
                            <button
                                key={r.id}
                                data-testid={`sim-list-option-${r.id}`}
                                onClick={() => onTap(r.id, r.title)}
                                className="w-full text-left text-xs px-2 py-1.5 rounded bg-black/30 border border-white/10 hover:bg-black/50"
                            >
                                <div>{r.title}</div>
                                {r.description && (
                                    <div className="text-[10px] opacity-70">{r.description}</div>
                                )}
                            </button>
                        ))}
                    </div>
                )}
                {m.kind === "interactive_buttons" && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                        {(payload.buttons || []).map((b) => (
                            <button
                                key={b.id}
                                data-testid={`sim-button-${b.id}`}
                                onClick={() => onTap(b.id, b.title)}
                                className="text-xs px-3 py-1 rounded-full bg-black/30 border border-white/10 hover:bg-black/50"
                            >
                                {b.title}
                            </button>
                        ))}
                    </div>
                )}
                <div className="text-[10px] opacity-60 mt-1 text-right font-mono">
                    {relative(m.created_at)}
                </div>
            </div>
        </div>
    );
}
